import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSupportRealtime } from '../hooks/useSupportRealtime';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { formatTime } from '../utils/format';
import {
  findNewestMediaMessage,
  getLocalMediaUrl,
  registerLocalMedia,
  revokeLocalMedia,
  transferLocalMedia,
} from '../utils/supportLocalMedia';
import { maybeInAppSupportNotify, registerSupportServiceWorker } from '../utils/supportPush';
import { SUPPORT_CREATE_EVENT, SUPPORT_OPEN_EVENT, takePendingSupportOpen, type SupportOpenDetail } from '../utils/supportOpen';
import { SupportPushControls } from './SupportPushControls';
import {
  MessageActionsMenu,
  MessageTime,
  MediaCaptionStack,
  VoicePlayer,
  VideoNotePlayer,
  CircleLivePreview,
  buildMessageRows,
  ForwardedLabel,
  ForwardQuotePreview,
  forwardAuthorLabel,
  forwardSnippet,
  isForwardedMessage,
  bindMessageLongPress,
  ClosedTicketBanner,
} from './supportMessageUi';
import {
  AttachMenu,
  EmojiPickerPanel,
  IconMic,
  IconSend,
  IconSmile,
  SelectionBar,
  SelectionCheck,
  REACTION_QUICK_EMOJIS,
  getLastReaction,
  setLastReaction,
} from './supportTelegramChrome';
import {
  filesFromClipboard,
  filesFromDataTransfer,
  filesFromFileList,
  pickVoiceRecorderMime,
  revokePendingMedia,
  toPendingMedia,
  upsertSupportTicket,
  sortSupportTickets,
  voiceFilenameForMime,
  type PendingMediaItem,
} from '../utils/supportMediaPick';
import {
  activityLabel,
  activityStateFromFiles,
  messageReceiptStatus,
  statusOrActivityLabel,
  type SupportActivityState,
} from '../utils/supportActivity';

export interface PortalSupportTicketListItem {
  id: number;
  user_id: number;
  subject: string;
  status: 'open' | 'answered' | 'closed';
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
  last_message_id?: number | null;
  last_message_type?: 'text' | 'image' | 'video' | 'voice' | 'album' | 'sticker' | 'gif' | 'video_note' | null;
  last_message_media_count?: number | null;
  last_message_author_role?: 'user' | 'support' | null;
}

export interface PortalSupportMessage {
  id: number;
  ticket_id: number;
  author_telegram_id: number;
  author_role: 'user' | 'support';
  message_type?: 'text' | 'image' | 'video' | 'voice' | 'album' | 'sticker' | 'gif' | 'video_note';
  body: string;
  created_at: string;
  delivered_at?: string | null;
  read_at?: string | null;
  edited_at?: string | null;
  forwarded_from_message_id?: number | null;
  attachment_name?: string;
  attachment_mime?: string;
  attachment_size?: number;
  duration_ms?: number;
  has_attachment?: boolean;
  attachments?: Array<{ index: number; name?: string | null; mime?: string | null; size?: number | null }>;
  author_display_name?: string | null;
  reactions?: Array<{
    emoji: string;
    count: number;
    me?: boolean;
    user_ids?: number[];
  }>;
  forwarded_bundle?: Array<{
    from_message_id: number;
    author_role: 'user' | 'support';
    message_type?: PortalSupportMessage['message_type'];
    body: string;
    attachment_name?: string;
    attachment_mime?: string;
    attachment_size?: number;
    duration_ms?: number;
    media_index: number;
    media_count: number;
    attachments?: PortalSupportMessage['attachments'];
  }>;
}

export interface PortalSupportTicketDetail extends PortalSupportTicketListItem {
  messages: PortalSupportMessage[];
}

function statusLabel(status: string): string {
  if (status === 'open') return 'Открыт';
  if (status === 'answered') return 'Есть ответ';
  if (status === 'closed') return 'Закрыт';
  return status;
}

function statusClass(status: string): string {
  if (status === 'open') return 'badge badge-warning';
  if (status === 'answered') return 'badge badge-success';
  return 'badge badge-muted';
}

function messagePreview(m: PortalSupportMessage | undefined): string | undefined {
  if (!m) return undefined;
  const t = m.message_type || 'text';
  const caption = (m.body || '').trim().replace(/\s+/g, ' ');
  if (t === 'image') return 'Фотография';
  if (t === 'video') return 'Видео';
  if (t === 'voice') return 'Голосовое сообщение';
  if (t === 'video_note') return 'Видеосообщение';
  if (t === 'sticker') return 'Стикер';
  if (t === 'gif') return 'GIF';
  if (t === 'album') return `${m.attachments?.length || 1} фото`;
  return caption || m.body || undefined;
}

function formatDuration(ms?: number): string {
  if (!ms || ms < 0) return '';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const POLL_MS = 8000;
const DETAIL_SYNC_MS = 12000;
const OPEN_TICKET_KEY = 'yp_portal_support_open_ticket';
const CREATE_TICKET_KEY = 'yp_portal_support_create';

function maxMessageId(messages: { id: number }[]): number {
  let max = 0;
  for (const m of messages) {
    if (m.id > max) max = m.id;
  }
  return max;
}

function ts(value: string | undefined): number {
  return value ? Date.parse(value) || 0 : 0;
}

function realMessages(messages: PortalSupportMessage[]): PortalSupportMessage[] {
  return messages.filter((m) => m.id > 0);
}

function messageIdsKey(messages: PortalSupportMessage[]): string {
  return realMessages(messages)
    .map((m) => m.id)
    .sort((a, b) => a - b)
    .join(',');
}

function hasReceiptProgress(
  incoming: PortalSupportTicketDetail,
  current: PortalSupportTicketDetail | null,
): boolean {
  if (!current) return false;
  const curMap = new Map(current.messages.map((m) => [m.id, m]));
  for (const m of incoming.messages) {
    if (m.id <= 0) continue;
    const old = curMap.get(m.id);
    if (!old) continue;
    if (!old.delivered_at && m.delivered_at) return true;
    if (!old.read_at && m.read_at) return true;
  }
  return false;
}

function hasMessageContentProgress(
  incoming: { messages: PortalSupportMessage[] },
  current: { messages: PortalSupportMessage[] } | null,
): boolean {
  if (!current) return false;
  const curMap = new Map(current.messages.map((m) => [m.id, m]));
  for (const m of incoming.messages) {
    if (m.id <= 0) continue;
    const old = curMap.get(m.id);
    if (!old) continue;
    if ((m.body || '') !== (old.body || '')) return true;
    if ((m.edited_at || null) !== (old.edited_at || null)) return true;
  }
  // Deletions: fewer real ids with same/lower max still count as progress when caller allows.
  const inIds = new Set(realMessages(incoming.messages).map((m) => m.id));
  return realMessages(current.messages).some((m) => !inIds.has(m.id));
}

function mergeMessagesById(
  prev: PortalSupportMessage[],
  incoming: PortalSupportMessage[],
  keepOptimistic = false,
): PortalSupportMessage[] {
  const prevMap = new Map<number, PortalSupportMessage>();
  for (const m of prev) {
    if (m.id > 0) prevMap.set(m.id, m);
  }
  // Incoming snapshot is authoritative for membership (edits + deletes).
  const map = new Map<number, PortalSupportMessage>();
  for (const m of incoming) {
    if (m.id <= 0) continue;
    const old = prevMap.get(m.id);
    if (old?.has_attachment && !m.has_attachment) {
      map.set(m.id, {
        ...m,
        has_attachment: true,
        attachment_name: m.attachment_name || old.attachment_name,
        attachment_mime: m.attachment_mime || old.attachment_mime,
        attachment_size: m.attachment_size ?? old.attachment_size,
        duration_ms: m.duration_ms ?? old.duration_ms,
        message_type: m.message_type !== 'text' ? m.message_type : old.message_type,
        delivered_at: m.delivered_at || old.delivered_at,
        read_at: m.read_at || old.read_at,
      });
    } else {
      map.set(m.id, {
        ...m,
        delivered_at: m.delivered_at || old?.delivered_at,
        read_at: m.read_at || old?.read_at,
      });
    }
  }
  const real = [...map.values()].sort((a, b) => a.id - b.id);
  // Only keep in-flight bubbles during soft live merges — never after a server fetch,
  // or a sent voice leaves a zombie "Отправка…" with revoked local media.
  if (!keepOptimistic) return real;
  const optimistics = prev.filter((m) => m.id < 0);
  return optimistics.length ? [...real, ...optimistics] : real;
}

/** True when incoming has messages we don't already show (higher max id, more rows, or new ids). */
function isRicherDetail(
  incoming: PortalSupportTicketDetail,
  current: PortalSupportTicketDetail | null,
): boolean {
  if (!current || current.id !== incoming.id) return true;
  const inReal = realMessages(incoming.messages);
  const curReal = realMessages(current.messages);
  const inMax = maxMessageId(inReal);
  const curMax = maxMessageId(curReal);
  if (inMax !== curMax) return inMax > curMax;
  if (inReal.length !== curReal.length) return inReal.length > curReal.length;
  const curIds = new Set(curReal.map((m) => m.id));
  return inReal.some((m) => !curIds.has(m.id));
}

function mergeTicketLists(
  prev: PortalSupportTicketListItem[],
  incoming: PortalSupportTicketListItem[],
): PortalSupportTicketListItem[] {
  const byId = new Map<number, PortalSupportTicketListItem>();
  for (const t of prev) byId.set(t.id, t);
  for (const t of incoming) {
    const old = byId.get(t.id);
    if (!old) {
      byId.set(t.id, t);
      continue;
    }
    if (ts(old.updated_at) > ts(t.updated_at)) {
      if (!old.last_message_preview && t.last_message_preview) {
        byId.set(t.id, { ...old, last_message_preview: t.last_message_preview });
      }
      continue;
    }
    byId.set(t.id, t);
  }
  return [...byId.values()];
}

function isTicketNotFoundError(error?: { code?: string; message?: string } | null): boolean {
  return error?.code === 'ticket_not_found';
}

/** Soft WS/poll guard: never accept a strictly older snapshot for the same ticket. */
function shouldApplySoftDetail(
  incoming: PortalSupportTicketDetail,
  current: PortalSupportTicketDetail | null,
): boolean {
  if (!current || current.id !== incoming.id) return true;
  const curReal = realMessages(current.messages);
  const inReal = realMessages(incoming.messages);
  const curMax = maxMessageId(curReal);
  const inMax = maxMessageId(inReal);
  // Stale snapshot — keep current UI so media players are not remounted.
  if (inMax < curMax) return false;
  if (inMax > curMax) return true;
  // Same max id: allow deletes / edits when clock moves forward (or equal with content change).
  if (ts(incoming.updated_at) > ts(current.updated_at)) return true;
  if (ts(incoming.updated_at) >= ts(current.updated_at)) {
    if (inReal.length !== curReal.length) return true;
    if (hasMessageContentProgress(incoming, current)) return true;
    return true;
  }
  return false;
}

/** True when incoming is strictly older than what we already show. */
function isStaleSnapshot(
  incoming: PortalSupportTicketDetail,
  current: PortalSupportTicketDetail | null,
): boolean {
  if (!current || current.id !== incoming.id) return false;
  const curReal = realMessages(current.messages);
  const inReal = realMessages(incoming.messages);
  const curMax = maxMessageId(curReal);
  const inMax = maxMessageId(inReal);
  if (inMax < curMax) return true;
  if (inMax > curMax) return false;
  // Fewer rows with same max: delete if clock advanced, else incomplete snapshot.
  if (inReal.length < curReal.length) {
    return ts(incoming.updated_at) < ts(current.updated_at);
  }
  return ts(incoming.updated_at) < ts(current.updated_at);
}

/**
 * Always UNION messages by id. Never drop a message we already have just because
 * a late/partial GET/WS snapshot omitted it (that caused "appear then vanish").
 */
function mergeServerDetail(
  prev: PortalSupportTicketDetail | null,
  incoming: PortalSupportTicketDetail,
  keepOptimistic = false,
): PortalSupportTicketDetail {
  const prevMsgs = prev && prev.id === incoming.id ? prev.messages : [];
  const messages = mergeMessagesById(prevMsgs, incoming.messages, keepOptimistic);
  const updated_at =
    prev && prev.id === incoming.id && ts(prev.updated_at) > ts(incoming.updated_at)
      ? prev.updated_at
      : incoming.updated_at;
  return { ...incoming, messages, updated_at };
}

function takeRefValue<T>(ref: { current: T | null }): T | null {
  const value = ref.current;
  ref.current = null;
  return value;
}

function lastPreview(detail: PortalSupportTicketDetail | null): string | undefined {
  if (!detail?.messages.length) return undefined;
  const msgs = realMessages(detail.messages);
  return messagePreview(msgs[msgs.length - 1] ?? detail.messages[detail.messages.length - 1]);
}

function lastAuthorRole(
  detail: PortalSupportTicketDetail | null,
): 'user' | 'support' | undefined {
  const msgs = detail ? realMessages(detail.messages) : [];
  if (!msgs.length) return undefined;
  return msgs[msgs.length - 1].author_role;
}

function listMetaFromDetail(detail: PortalSupportTicketDetail): {
  last_message_preview?: string;
  last_message_id?: number | null;
  last_message_type?: PortalSupportTicketListItem['last_message_type'];
  last_message_media_count?: number | null;
  last_message_author_role?: 'user' | 'support' | null;
} {
  const msgs = realMessages(detail.messages);
  const last = msgs[msgs.length - 1];
  if (!last) {
    return {
      last_message_preview: undefined,
      last_message_id: null,
      last_message_type: null,
      last_message_media_count: null,
      last_message_author_role: null,
    };
  }
  const type = (last.message_type || 'text') as NonNullable<
    PortalSupportTicketListItem['last_message_type']
  >;
  return {
    last_message_preview: lastPreview(detail),
    last_message_id: last.id > 0 ? last.id : null,
    last_message_type: type,
    last_message_media_count:
      type === 'album'
        ? Math.max(1, last.attachments?.length || 1)
        : type === 'image' ||
            type === 'video' ||
            type === 'sticker' ||
            type === 'gif' ||
            Boolean(last.has_attachment)
          ? 1
          : 0,
    last_message_author_role: last.author_role,
  };
}

function ticketListTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return formatTime(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return 'Вчера';
  }
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function IconTrash() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9 3h6l1 2h4v2H4V5h4Zm1 6h2v9h-2Zm4 0h2v9h-2ZM7 9h2v9H7Zm-1 12h12l1-12H5Z"
      />
    </svg>
  );
}

function IconMicOff() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M19.1 15.1 17.7 13.7A6.9 6.9 0 0 0 19 10h-2a5 5 0 0 1-1.1 3.1l-1.5-1.5A3 3 0 0 0 15 10V5a3 3 0 0 0-6 0v.2L3.7 1.9 2.3 3.3l18 18 1.4-1.4ZM12 18a7 7 0 0 1-7-7H3a9 9 0 0 0 8 8.9V22h2v-2.1a8.9 8.9 0 0 0 4.2-1.6l-1.5-1.5A6.9 6.9 0 0 1 12 18Zm-3-8.2V10a3 3 0 0 0 3.8 2.9Z"
      />
    </svg>
  );
}

function IconCircle() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="7.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="3.2" fill="currentColor" />
    </svg>
  );
}

function MediaLightbox({
  items,
  index,
  onClose,
}: {
  items: Array<{ src: string; alt?: string; kind?: 'image' | 'video' }>;
  index: number;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState(index);
  useEffect(() => {
    setCurrent(index);
  }, [index, items]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (items.length < 2) return;
      if (e.key === 'ArrowLeft') setCurrent((value) => (value + items.length - 1) % items.length);
      if (e.key === 'ArrowRight') setCurrent((value) => (value + 1) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items.length, onClose]);
  const item = items[current] || items[0];
  if (!item) return null;
  const isVideo = item.kind === 'video';

  return (
    <div className="support-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <button
        type="button"
        className="support-lightbox-close"
        aria-label="Закрыть"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <span aria-hidden>×</span>
      </button>
      {isVideo ? (
        <video
          src={item.src}
          className="support-lightbox-image"
          controls
          playsInline
          autoPlay
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={item.src}
          alt={item.alt || 'Фото'}
          className="support-lightbox-image"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {items.length > 1 ? (
        <>
          <button
            type="button"
            className="support-lightbox-nav is-prev"
            aria-label="Предыдущее"
            onClick={(e) => {
              e.stopPropagation();
              setCurrent((current + items.length - 1) % items.length);
            }}
          >
            <span aria-hidden>‹</span>
          </button>
          <button
            type="button"
            className="support-lightbox-nav is-next"
            aria-label="Следующее"
            onClick={(e) => {
              e.stopPropagation();
              setCurrent((current + 1) % items.length);
            }}
          >
            <span aria-hidden>›</span>
          </button>
        </>
      ) : null}
    </div>
  );
}

function MessageBody({
  message,
  onOpenImage,
  onOpenGallery,
  mediaAlign = 'end',
  onMediaLayout,
  mediaIndex = 0,
}: {
  message: PortalSupportMessage;
  onOpenImage: (url: string, alt?: string) => void;
  onOpenGallery?: (items: Array<{ src: string; alt?: string }>, startIndex: number) => void;
  mediaAlign?: 'end' | 'start';
  onMediaLayout?: () => void;
  mediaIndex?: number;
}) {
  const type = message.message_type || 'text';
  const localUrl = getLocalMediaUrl(message.id);
  const bust = `${message.id}-${message.attachment_size ?? 0}-${message.created_at ?? ''}`;
  const serverUrl =
    message.has_attachment && message.id > 0 ? api.supportMediaUrl(message.id, bust, mediaIndex) : null;
  const mediaUrl = localUrl || serverUrl;
  const onMediaReady = () => {
    if (localUrl && serverUrl) revokeLocalMedia(message.id);
    onMediaLayout?.();
  };
  const albumItems =
    type === 'album'
      ? message.attachments && message.attachments.length > 0
        ? message.attachments
        : [{ index: 0 }]
      : [];

  const openAlbumAt = (localIdx: number) => {
    const items = albumItems.map((item, i) => {
      const idx = mediaIndex + (item.index ?? i);
      return {
        src: api.supportMediaUrl(message.id, `${bust}-${idx}`, idx),
        alt: item.name || 'Медиа',
      };
    });
    if (onOpenGallery) onOpenGallery(items, localIdx);
    else onOpenImage(items[localIdx]?.src || '', items[localIdx]?.alt);
  };

  return (
    <div className="support-bubble-body">
      {type === 'album' && message.id > 0 && (
        <div className={`support-album-grid cols-${Math.min(albumItems.length, 3)}`}>
          {albumItems.map((item, localIdx) => {
            const idx = mediaIndex + (item.index ?? localIdx);
            const url = api.supportMediaUrl(message.id, `${bust}-${idx}`, idx);
            const isVideo = (item.mime || '').startsWith('video/');
            return (
              <button
                key={idx}
                type="button"
                className="support-album-cell"
                onClick={() => openAlbumAt(localIdx)}
              >
                {isVideo ? (
                  <video src={url} className="support-album-thumb" muted playsInline preload="metadata" />
                ) : (
                  <img
                    src={url}
                    alt={item.name || 'Фото'}
                    className="support-album-thumb"
                    onLoad={onMediaReady}
                  />
                )}
              </button>
            );
          })}
          {message.body.trim() ? <div className="support-bubble-text">{message.body}</div> : null}
        </div>
      )}
      {type === 'image' && mediaUrl && (
        <MediaCaptionStack
          caption={message.body}
          mediaUrl={mediaUrl}
          alt={message.attachment_name || 'Фото'}
          align={mediaAlign}
          variant="portal"
          onOpen={() => onOpenImage(mediaUrl, message.attachment_name || 'Фото')}
          onMediaReady={onMediaReady}
        />
      )}
      {type === 'sticker' && mediaUrl && (
        <div className="support-sticker">
          <img
            src={mediaUrl}
            alt="Стикер"
            className="support-sticker-image"
            draggable={false}
            onLoad={onMediaReady}
          />
        </div>
      )}
      {type === 'gif' && mediaUrl && (
        <div className="support-gif">
          <button type="button" className="support-gif-link" onClick={() => onOpenImage(mediaUrl, 'GIF')}>
            <img src={mediaUrl} alt="GIF" className="support-gif-image" onLoad={onMediaReady} />
            <span className="support-gif-badge">GIF</span>
          </button>
        </div>
      )}
      {type === 'video' && mediaUrl && (
        <MediaCaptionStack
          caption={message.body}
          mediaUrl={mediaUrl}
          align={mediaAlign}
          variant="portal"
          kind="video"
          onMediaReady={onMediaReady}
        />
      )}
      {type === 'voice' && mediaUrl && (
        <VoicePlayer src={mediaUrl} durationMs={message.duration_ms} onReady={onMediaReady} />
      )}
      {type === 'video_note' && mediaUrl && (
        <VideoNotePlayer src={mediaUrl} durationMs={message.duration_ms} onReady={onMediaReady} />
      )}
      {type === 'text' && message.body ? <div className="support-bubble-text">{message.body}</div> : null}
      {type !== 'text' && type !== 'album' && !mediaUrl && message.id < 0 ? (
        <div className="muted">
          {type === 'voice'
            ? 'Отправка голосового…'
            : type === 'video_note'
              ? 'Отправка видеосообщения…'
              : type === 'image'
                ? 'Отправка фото…'
                : type === 'video'
                  ? 'Отправка видео…'
                  : 'Отправка…'}
        </div>
      ) : null}
      {type === 'album' && message.id < 0 ? <div className="muted">Отправка альбома…</div> : null}
    </div>
  );
}

const NEAR_BOTTOM_PX = 100;

function isMessagesNearBottom(el: HTMLDivElement | null): boolean {
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
}

export function SupportSection() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<PortalSupportTicketListItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PortalSupportTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTicketRef = useRef<number | null>(null);
  const [highlightMessageId, setHighlightMessageId] = useState<number | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [reply, setReply] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordMs, setRecordMs] = useState(0);
  const [pendingMedia, setPendingMedia] = useState<PendingMediaItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [pendingVoice, setPendingVoice] = useState<{
    blob: Blob;
    durationMs: number;
    url: string;
    kind: 'voice' | 'circle';
  } | null>(null);
  const [captureMode, setCaptureMode] = useState<'voice' | 'circle'>('voice');
  const [circleMuted, setCircleMuted] = useState(true);
  const [recordingLocked, setRecordingLocked] = useState(false);
  const [captureGesture, setCaptureGesture] = useState<{
    originX: number;
    originY: number;
    lift: number;
    lockProgress: number;
    locked: boolean;
    mode: 'voice' | 'circle';
  } | null>(null);
  const [circleLiveStream, setCircleLiveStream] = useState<MediaStream | null>(null);
  const [lightbox, setLightbox] = useState<{
    items: Array<{ src: string; alt?: string; kind?: 'image' | 'video' }>;
    index: number;
  } | null>(null);
  const [pendingForward, setPendingForward] = useState<PortalSupportMessage[]>([]);
  const [peerActivity, setPeerActivity] = useState<SupportActivityState | null>(null);
  const [ticketActivity, setTicketActivity] = useState<Record<number, SupportActivityState>>({});
  const ticketActivityTimersRef = useRef<Record<number, number>>({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [menu, setMenu] = useState<{
    message: PortalSupportMessage;
    x: number;
    y: number;
  } | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const longPressCancelRef = useRef<(() => void) | null>(null);
  const lastTapRef = useRef<{ id: number; at: number } | null>(null);
  const messagesInnerRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const createFileInputRef = useRef<HTMLInputElement | null>(null);
  const replyInputRef = useRef<HTMLTextAreaElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStartedAt = useRef(0);
  const recordTimerRef = useRef<number | null>(null);
  const recordStreamRef = useRef<MediaStream | null>(null);
  const cancelRecordingRef = useRef(false);
  const recordMaxTimerRef = useRef<number | null>(null);
  const recordingLockedRef = useRef(false);
  const circleHoldActiveRef = useRef(false);
  const captureModeRef = useRef(captureMode);
  const circleMutedRef = useRef(circleMuted);
  captureModeRef.current = captureMode;
  circleMutedRef.current = circleMuted;
  const typingIdleTimerRef = useRef<number | null>(null);
  const liveActivityTimerRef = useRef<number | null>(null);
  const lastSentActivityRef = useRef<SupportActivityState | null>(null);
  const typingSentAtRef = useRef(0);
  const sendReceiptRef = useRef<(ticketId: number, kind: 'delivered' | 'read', upTo: number) => void>(
    () => undefined,
  );
  const selectedIdRef = useRef<number | null>(null);
  const detailRef = useRef<PortalSupportTicketDetail | null>(null);
  const ticketsRef = useRef<PortalSupportTicketListItem[]>([]);
  const detailCacheRef = useRef<Map<number, PortalSupportTicketDetail>>(new Map());
  const seenPreviewRef = useRef<Map<number, string>>(new Map());
  const showCreateRef = useRef(false);
  const busyRef = useRef(false);
  const detailFetchSeq = useRef(0);
  const ticketGoneMissesRef = useRef(0);
  const detailLoadingRef = useRef(false);
  const pendingDetailSyncRef = useRef(false);
  const queuedWsDetailRef = useRef<PortalSupportTicketDetail | null>(null);
  const lastDetailHardSyncRef = useRef(0);
  const wasNearBottomRef = useRef(true);
  const restoredTicketRef = useRef(false);

  selectedIdRef.current = selectedId;
  detailRef.current = detail;
  ticketsRef.current = tickets;
  showCreateRef.current = showCreate;
  busyRef.current = busy;

  const clearPendingFile = useCallback(() => {
    setPendingMedia((prev) => {
      revokePendingMedia(prev);
      return [];
    });
  }, []);

  const clearPendingVoice = useCallback(() => {
    setPendingVoice((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  const clearRecordingActivityPulse = useCallback(() => {
    if (liveActivityTimerRef.current != null) {
      window.clearInterval(liveActivityTimerRef.current);
      liveActivityTimerRef.current = null;
    }
  }, []);

  const clearLiveActivityPulse = clearRecordingActivityPulse;

  const addPendingFiles = useCallback(
    (files: File[]) => {
      if (!files.length) return;
      clearPendingVoice();
      setPendingMedia((prev) => [...prev, ...toPendingMedia(files)]);
    },
    [clearPendingVoice],
  );

  const openLightbox = useCallback((src: string, alt?: string) => {
    setLightbox({ items: [{ src, alt }], index: 0 });
  }, []);

  const openLightboxGallery = useCallback(
    (items: Array<{ src: string; alt?: string }>, startIndex = 0) => {
      if (!items.length) return;
      setLightbox({ items, index: Math.max(0, Math.min(startIndex, items.length - 1)) });
    },
    [],
  );

  const closeLightbox = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    return () => {
      setPendingMedia((prev) => {
        revokePendingMedia(prev);
        return [];
      });
      if (pendingVoice?.url) URL.revokeObjectURL(pendingVoice.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke only on unmount
  }, []);

  const pinMessagesToBottom = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    wasNearBottomRef.current = true;
  }, []);

  // Keep following the bottom while stick is on — do NOT clear stick here (media/layout
  // still grows after the first paint; clearing early left the view stuck at the top).
  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return;
    pinMessagesToBottom();
  }, [detail?.messages, detail?.id, pinMessagesToBottom]);

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    pinMessagesToBottom();
    const raf = requestAnimationFrame(() => {
      pinMessagesToBottom();
      requestAnimationFrame(pinMessagesToBottom);
    });
    const timers = [50, 150, 400].map((ms) => window.setTimeout(pinMessagesToBottom, ms));
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [detail?.messages, detail?.id, pinMessagesToBottom]);

  useEffect(() => {
    const el = messagesRef.current;
    const inner = messagesInnerRef.current;
    if (!el) return;
    const onScroll = () => {
      const near = isMessagesNearBottom(el);
      wasNearBottomRef.current = near;
      // User scrolled up — stop auto-follow until next forceScroll/open.
      if (!near) stickToBottomRef.current = false;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Observe the *inner* content: the scroll container has fixed height, so observing
    // only `el` never fires when messages/images grow scrollHeight.
    const ro = new ResizeObserver(() => {
      if (!wasNearBottomRef.current && !stickToBottomRef.current) return;
      pinMessagesToBottom();
    });
    ro.observe(inner ?? el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [detail?.id, pinMessagesToBottom]);

  const onMessageMediaReady = useCallback(() => {
    if (!wasNearBottomRef.current && !stickToBottomRef.current) return;
    requestAnimationFrame(() => {
      pinMessagesToBottom();
    });
  }, [pinMessagesToBottom]);

  const focusOriginalMessage = useCallback((messageId: number) => {
    if (!messageId || messageId <= 0) return;
    const root = messagesRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightMessageId(messageId);
    if (highlightTimerRef.current != null) window.clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightMessageId((cur) => (cur === messageId ? null : cur));
      highlightTimerRef.current = null;
    }, 1400);
  }, []);

  const rememberDetail = useCallback((ticket: PortalSupportTicketDetail) => {
    detailCacheRef.current.set(ticket.id, ticket);
  }, []);

  const applyDetail = useCallback(
    (
      incoming: PortalSupportTicketDetail,
      opts?: { forceScroll?: boolean; replace?: boolean; hard?: boolean; fromFetch?: boolean },
    ) => {
      const selected = selectedIdRef.current;
      if (selected != null && incoming.id !== selected) {
        const cached = detailCacheRef.current.get(incoming.id);
        rememberDetail(
          cached
            ? { ...incoming, messages: mergeMessagesById(cached.messages, incoming.messages) }
            : incoming,
        );
        return false;
      }

      const prev = detailRef.current;
      const wasNearBottom = isMessagesNearBottom(messagesRef.current);

      // Late/stale GET must never wipe messages that WS already delivered.
      if (opts?.fromFetch && isStaleSnapshot(incoming, prev)) {
        return false;
      }

      let next = incoming;

      if (opts?.hard || opts?.replace) {
        if (opts?.hard && isStaleSnapshot(incoming, prev)) {
          return false;
        }
        // Server fetch/replace must drop optimistic zombies; soft live path keeps them.
        next = mergeServerDetail(prev, incoming, false);
      } else if (prev && prev.id === incoming.id) {
        // Drop optimistic once live snapshot already has newer real messages (sent voice confirmed).
        const keepOpt = !isRicherDetail(incoming, {
          ...prev,
          messages: realMessages(prev.messages),
        });
        if (!shouldApplySoftDetail(incoming, prev)) {
          const merged = mergeMessagesById(prev.messages, incoming.messages, keepOpt);
          const msgsChanged = messageIdsKey(merged) !== messageIdsKey(prev.messages);
          const contentChanged = hasMessageContentProgress(
            { messages: merged },
            { messages: prev.messages },
          );
          const receiptsChanged = hasReceiptProgress(incoming, prev);
          const statusForce =
            incoming.status !== prev.status &&
            (incoming.status === 'closed' || ts(incoming.updated_at) >= ts(prev.updated_at));
          if (!msgsChanged && !contentChanged && !statusForce && !receiptsChanged) {
            return false;
          }
          const absorbed = {
            ...prev,
            messages: msgsChanged || contentChanged || receiptsChanged ? merged : prev.messages,
            status: statusForce ? incoming.status : prev.status,
            updated_at:
              ts(incoming.updated_at) >= ts(prev.updated_at) ? incoming.updated_at : prev.updated_at,
          };
          detailRef.current = absorbed;
          rememberDetail(absorbed);
          if (opts?.forceScroll || wasNearBottom) {
            stickToBottomRef.current = true;
          }
          const absPreview = lastPreview(absorbed);
          if (absPreview) seenPreviewRef.current.set(absorbed.id, absPreview);
          setTickets((prevTickets) => {
            const mapped = prevTickets.map((t) =>
              t.id === absorbed.id
                ? {
                    ...t,
                    status: absorbed.status,
                    updated_at:
                      ts(absorbed.updated_at) >= ts(t.updated_at) ? absorbed.updated_at : t.updated_at,
                    ...listMetaFromDetail(absorbed),
                  }
                : t,
            );
            return statusForce ? sortSupportTickets(mapped, ts) : mapped;
          });
          setDetail(absorbed);
          return true;
        }
        const mergedMsgs = mergeMessagesById(prev.messages, incoming.messages, keepOpt);
        next = {
          ...incoming,
          updated_at:
            ts(incoming.updated_at) >= ts(prev.updated_at) ? incoming.updated_at : prev.updated_at,
          messages: mergedMsgs,
        };
        if (
          messageIdsKey(prev.messages) === messageIdsKey(next.messages) &&
          prev.status === next.status &&
          prev.updated_at === next.updated_at &&
          !hasReceiptProgress(next, prev) &&
          !hasMessageContentProgress(next, prev)
        ) {
          rememberDetail(next);
          return false;
        }
      }

      const grew =
        !prev ||
        prev.id !== next.id ||
        realMessages(next.messages).length > realMessages(prev.messages).length ||
        maxMessageId(next.messages) > maxMessageId(prev.messages);
      detailRef.current = next;
      rememberDetail(next);
      // Do NOT bump detailFetchSeq here — that cancelled in-flight catch-up GETs
      // right after a live WS message and left the UI stuck until the next poll.
      const preview = lastPreview(next);
      if (preview) seenPreviewRef.current.set(next.id, preview);
      setTickets((prevTickets) => {
        const exists = prevTickets.some((t) => t.id === next.id);
        if (!exists) return prevTickets;
        const mapped = prevTickets.map((t) => {
          if (t.id !== next.id) return t;
          const newerMeta = ts(next.updated_at) > ts(t.updated_at);
          return {
            ...t,
            status: next.status,
            updated_at: newerMeta || ts(next.updated_at) >= ts(t.updated_at) ? next.updated_at : t.updated_at,
            ...listMetaFromDetail(next),
          };
        });
        const before = prevTickets.find((t) => t.id === next.id);
        const after = mapped.find((t) => t.id === next.id);
        if (
          before &&
          after &&
          (before.status !== after.status || ts(after.updated_at) > ts(before.updated_at))
        ) {
          return sortSupportTickets(mapped, ts);
        }
        return mapped;
      });
      if (opts?.forceScroll || (grew && wasNearBottom)) {
        stickToBottomRef.current = true;
      }
      setDetail(next);
      let maxPeer = 0;
      for (const m of next.messages) {
        if (m.author_role === 'support' && m.id > maxPeer) maxPeer = m.id;
      }
      if (maxPeer > 0) {
        sendReceiptRef.current(next.id, 'delivered', maxPeer);
        sendReceiptRef.current(next.id, 'read', maxPeer);
      }
      return true;
    },
    [rememberDetail],
  );

  const clearGoneTicket = useCallback((id: number, message?: string) => {
    if (selectedIdRef.current !== id) return;
    ticketGoneMissesRef.current = 0;
    detailCacheRef.current.delete(id);
    seenPreviewRef.current.delete(id);
    setSelectedId(null);
    selectedIdRef.current = null;
    setDetail(null);
    detailRef.current = null;
    setSelectedIds([]);
    setEmojiOpen(false);
    setShowCreate(false);
    pendingDetailSyncRef.current = false;
    takeRefValue(queuedWsDetailRef);
    clearPendingFile();
    clearPendingVoice();
    try {
      const raw = sessionStorage.getItem(OPEN_TICKET_KEY);
      if (raw && Number(raw) === id) sessionStorage.removeItem(OPEN_TICKET_KEY);
    } catch {
      /* ignore */
    }
    setTickets((prev) => prev.filter((t) => t.id !== id));
    setError(message ?? null);
  }, [clearPendingFile, clearPendingVoice]);

  const noteTicketNotFound = useCallback(
    (id: number) => {
      if (selectedIdRef.current !== id) return;
      ticketGoneMissesRef.current += 1;
      if (ticketGoneMissesRef.current < 3) {
        setError('Не удалось загрузить тикет, повторяем…');
        return;
      }
      clearGoneTicket(id, 'Тикет не найден');
    },
    [clearGoneTicket],
  );

  const syncDetailFromServer = useCallback(
    async (forceScroll = false) => {
      const id = selectedIdRef.current;
      if (id == null || showCreateRef.current) return;
      if (detailLoadingRef.current) {
        pendingDetailSyncRef.current = true;
        return;
      }
      const seq = ++detailFetchSeq.current;
      const res = await api.getSupportTicket(id);
      if (selectedIdRef.current !== id) return;
      // Prefer richer payloads even if a newer fetch started — don't leave the UI stuck stale.
      if (seq !== detailFetchSeq.current) {
        if (!res.success || !res.data || !isRicherDetail(res.data, detailRef.current)) {
          return;
        }
      }
      if (res.success && res.data) {
        ticketGoneMissesRef.current = 0;
        lastDetailHardSyncRef.current = Date.now();
        applyDetail(res.data, { replace: true, forceScroll, fromFetch: true });
      } else if (isTicketNotFoundError(res.error)) {
        noteTicketNotFound(id);
      }
    },
    [applyDetail, noteTicketNotFound],
  );

  const refreshDetailIfNeeded = useCallback(
    async (listItem: PortalSupportTicketListItem | undefined, force = false) => {
      const id = selectedIdRef.current;
      if (id == null || showCreateRef.current) return;
      if (detailLoadingRef.current) {
        pendingDetailSyncRef.current = true;
        return;
      }
      const current = detailRef.current;
      const missingFromList = !listItem || listItem.id !== id;
      // Stale list poll must not wipe an open ticket.
      if (missingFromList && !force) return;
      const due = Date.now() - lastDetailHardSyncRef.current >= DETAIL_SYNC_MS;
      const previewMismatch = Boolean(
        listItem &&
          listItem.id === id &&
          listItem.last_message_preview &&
          listItem.last_message_preview !== lastPreview(current),
      );
      const listNewer = Boolean(
        listItem &&
          listItem.id === id &&
          (!current ||
            current.id !== id ||
            ts(listItem.updated_at) > ts(current.updated_at) ||
            previewMismatch),
      );
      if (!force && !missingFromList && !due && !listNewer && !previewMismatch) return;

      const seq = ++detailFetchSeq.current;
      const res = await api.getSupportTicket(id);
      if (selectedIdRef.current !== id) return;
      if (seq !== detailFetchSeq.current) {
        if (!res.success || !res.data || !isRicherDetail(res.data, detailRef.current)) {
          return;
        }
      }
      if (res.success && res.data) {
        ticketGoneMissesRef.current = 0;
        lastDetailHardSyncRef.current = Date.now();
        applyDetail(res.data, { replace: true, forceScroll: false, fromFetch: true });
      } else if (isTicketNotFoundError(res.error)) {
        noteTicketNotFound(id);
      }
    },
    [applyDetail, noteTicketNotFound],
  );

  const loadList = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    const res = await api.listSupportTickets();
    if (res.success) {
      const incoming = res.data ?? [];
      let merged = silent ? mergeTicketLists(ticketsRef.current, incoming) : incoming;
      // If an open ticket detail is already loaded, prefer its preview immediately.
      const open = detailRef.current;
      if (open) {
        const preview = lastPreview(open);
        merged = merged.map((t) =>
          t.id === open.id
            ? {
                ...t,
                status: open.status,
                updated_at: ts(open.updated_at) >= ts(t.updated_at) ? open.updated_at : t.updated_at,
                last_message_preview: preview || t.last_message_preview,
                last_message_author_role: lastAuthorRole(open) ?? t.last_message_author_role,
              }
            : t,
        );
      }
      setTickets(sortSupportTickets(merged, ts));
      const id = selectedIdRef.current;
      if (id != null) {
        await refreshDetailIfNeeded(merged.find((t) => t.id === id), !silent);
      }
    } else if (!silent) {
      setError(res.error?.message ?? 'Не удалось загрузить тикеты');
    }
    if (!silent) setLoading(false);
  }, [refreshDetailIfNeeded]);

  const openTicket = useCallback(
    async (id: number, opts?: { fresh?: boolean }) => {
      const fresh = opts?.fresh === true;
      setSelectedId(id);
      selectedIdRef.current = id;
      setShowCreate(false);
      setError(null);
      setSelectedIds([]);
      setEmojiOpen(false);
      setPeerActivity(null);
      lastSentActivityRef.current = null;
      clearRecordingActivityPulse();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        cancelRecordingRef.current = true;
        mediaRecorderRef.current.stop();
      }
      clearPendingFile();
      clearPendingVoice();
      try {
        sessionStorage.setItem(OPEN_TICKET_KEY, String(id));
      } catch {
        /* ignore */
      }

      detailLoadingRef.current = true;
      pendingDetailSyncRef.current = false;
      takeRefValue(queuedWsDetailRef);
      detailFetchSeq.current += 1; // cancel background syncs, but we won't discard THIS response via seq
      if (fresh) {
        detailCacheRef.current.delete(id);
      }
      // Prefer in-memory thread, then warm cache — avoids empty flash + lost WS race.
      const keepPrev = fresh
        ? null
        : detailRef.current?.id === id
          ? detailRef.current
          : detailCacheRef.current.get(id) ?? null;
      // Always land on the latest message when opening a ticket.
      stickToBottomRef.current = true;
      wasNearBottomRef.current = true;
      if (keepPrev) {
        detailRef.current = keepPrev;
        setDetail(keepPrev);
      } else {
        detailRef.current = null;
        setDetail(null);
      }

      try {
        const res = await api.getSupportTicket(id);
        if (selectedIdRef.current !== id) return;
        if (res.success && res.data) {
          ticketGoneMissesRef.current = 0;
          lastDetailHardSyncRef.current = Date.now();
          // Seed prev from keepPrev so merge unions instead of replacing from empty.
          if (keepPrev) detailRef.current = keepPrev;
          const preview = lastPreview(res.data);
          if (preview) seenPreviewRef.current.set(id, preview);
          applyDetail(res.data, { replace: true, hard: true, forceScroll: true, fromFetch: true });

          // List preview ahead of chat — retry a few times (stale pool reads can lag briefly).
          const listRow = ticketsRef.current.find((t) => t.id === id);
          const previewAhead = () =>
            Boolean(
              listRow?.last_message_preview &&
                listRow.last_message_preview !== lastPreview(detailRef.current),
            );
          if (previewAhead()) {
            for (const delay of [0, 250, 700]) {
              if (delay > 0) await new Promise((r) => window.setTimeout(r, delay));
              if (selectedIdRef.current !== id || !previewAhead()) break;
              const retry = await api.getSupportTicket(id);
              if (selectedIdRef.current !== id) return;
              if (retry.success && retry.data) {
                applyDetail(retry.data, {
                  replace: true,
                  hard: true,
                  forceScroll: true,
                  fromFetch: true,
                });
                const p2 = lastPreview(retry.data);
                if (p2) seenPreviewRef.current.set(id, p2);
              } else if (isTicketNotFoundError(retry.error)) {
                noteTicketNotFound(id);
                return;
              }
            }
          }
        } else {
          if (isTicketNotFoundError(res.error)) {
            noteTicketNotFound(id);
            return;
          }
          setError(res.error?.message ?? 'Тикет не найден');
        }
      } finally {
        if (selectedIdRef.current === id) {
          detailLoadingRef.current = false;
          const queued = takeRefValue(queuedWsDetailRef);
          if (queued != null && queued.id === id) {
            applyDetail(queued, { forceScroll: false });
          }
          const needSync = pendingDetailSyncRef.current;
          pendingDetailSyncRef.current = false;
          // Always catch up once after open — closes restore/WS race (missed snapshots while loading).
          void syncDetailFromServer(needSync || fresh);
          // Burst catch-up after open/hard-refresh: first GET can briefly see a stale DB
          // snapshot until the connection pool recycles / autocommit fix is live.
          for (const delay of fresh ? [200, 600, 1500, 3500] : [400, 1200]) {
            window.setTimeout(() => {
              if (selectedIdRef.current !== id || detailLoadingRef.current) return;
              void syncDetailFromServer(delay === (fresh ? 200 : 400));
            }, delay);
          }
        }
      }
    },
    [applyDetail, noteTicketNotFound, clearPendingFile, clearPendingVoice, clearRecordingActivityPulse, syncDetailFromServer],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load list + open ticket in parallel — don't wait for list before chat.
      const listPromise = loadList();
      if (!restoredTicketRef.current) {
        restoredTicketRef.current = true;
        try {
          const params = new URLSearchParams(window.location.search);
          const wantCreate =
            params.get('create') === '1' ||
            params.get('create') === 'true' ||
            sessionStorage.getItem(CREATE_TICKET_KEY) === '1';
          if (wantCreate) {
            try {
              sessionStorage.removeItem(CREATE_TICKET_KEY);
            } catch {
              /* ignore */
            }
            setShowCreate(true);
            setSelectedId(null);
            selectedIdRef.current = null;
            setDetail(null);
            detailRef.current = null;
          } else {
            // URL deep-link or one-shot open from notification / toast (not last browsed ticket).
            const fromUrl = Number(params.get('ticket') || 0);
            const pending = takePendingSupportOpen();
            const id = fromUrl > 0 ? fromUrl : pending?.ticketId ?? 0;
            if (id > 0) {
              void openTicket(id, {
                fresh: fromUrl > 0 ? true : pending?.fresh !== false,
              });
            }
          }
        } catch {
          /* ignore */
        }
      }
      await listPromise;
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<SupportOpenDetail>).detail;
      const id = Number(detail?.ticketId || 0);
      if (id > 0) void openTicket(id, { fresh: detail?.fresh !== false });
    };
    const onCreate = () => {
      try {
        sessionStorage.removeItem(CREATE_TICKET_KEY);
      } catch {
        /* ignore */
      }
      setShowCreate(true);
      setSelectedId(null);
      selectedIdRef.current = null;
      setDetail(null);
      detailRef.current = null;
      setEmojiOpen(false);
      clearPendingFile();
      clearPendingVoice();
    };
    window.addEventListener(SUPPORT_OPEN_EVENT, onOpen);
    window.addEventListener(SUPPORT_CREATE_EVENT, onCreate);
    return () => {
      window.removeEventListener(SUPPORT_OPEN_EVENT, onOpen);
      window.removeEventListener(SUPPORT_CREATE_EVENT, onCreate);
    };
  }, [clearPendingFile, clearPendingVoice, openTicket]);

  useEffect(() => {
    void registerSupportServiceWorker(`${import.meta.env.BASE_URL}sw.js`);
  }, []);

  useEffect(() => {
    const tick = () => {
      void loadList({ silent: true });
    };
    const timer = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadList]);

  // Tab focus / visibility — catch up immediately if WS was quiet.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void loadList({ silent: true });
      if (selectedIdRef.current == null) return;
      if (detailLoadingRef.current) {
        pendingDetailSyncRef.current = true;
        return;
      }
      void syncDetailFromServer(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [loadList, syncDetailFromServer]);

  const { sendActivity, sendReceipt } = useSupportRealtime(
    (event) => {
      if (event.type === 'activity') {
        if (event.author_role !== 'support') return;
        const tid = event.ticket_id;
        const clearOne = () => {
          setTicketActivity((prev) => {
            if (!(tid in prev)) return prev;
            const next = { ...prev };
            delete next[tid];
            return next;
          });
          if (selectedIdRef.current === tid) setPeerActivity(null);
        };
        if (event.state === 'idle') {
          clearOne();
          const t = ticketActivityTimersRef.current[tid];
          if (t != null) {
            window.clearTimeout(t);
            delete ticketActivityTimersRef.current[tid];
          }
          return;
        }
        setTicketActivity((prev) => ({ ...prev, [tid]: event.state }));
        if (selectedIdRef.current === tid) setPeerActivity(event.state);
        const prevTimer = ticketActivityTimersRef.current[tid];
        if (prevTimer != null) window.clearTimeout(prevTimer);
        ticketActivityTimersRef.current[tid] = window.setTimeout(() => {
          delete ticketActivityTimersRef.current[tid];
          clearOne();
        }, 5000);
        return;
      }
      if (event.type === 'ticket_snapshot') {
        if (event.ticket?.last_message_author_role === 'support' && event.ticket.last_message_id) {
          sendReceipt(event.ticket_id, 'delivered', event.ticket.last_message_id);
        }
        if (
          selectedIdRef.current === event.ticket_id &&
          event.ticket?.last_message_author_role === 'support'
        ) {
          setPeerActivity(null);
          setTicketActivity((prev) => {
            if (!(event.ticket_id in prev)) return prev;
            const next = { ...prev };
            delete next[event.ticket_id];
            return next;
          });
        }
        if (event.ticket) {
          setTickets((prev) => {
            const row: PortalSupportTicketListItem = {
              id: event.ticket!.id,
              user_id: event.ticket!.user_id,
              subject: event.ticket!.subject,
              status: event.ticket!.status,
              created_at: event.ticket!.created_at,
              updated_at: event.ticket!.updated_at,
              last_message_preview: event.ticket!.last_message_preview,
              last_message_id: event.ticket!.last_message_id,
              last_message_type: event.ticket!.last_message_type,
              last_message_media_count: event.ticket!.last_message_media_count,
              last_message_author_role: event.ticket!.last_message_author_role,
            };
            return upsertSupportTicket(prev, row, ts);
          });
          if (
            event.ticket.last_message_author_role === 'support' &&
            event.ticket.last_message_id
          ) {
            const preview = event.ticket.last_message_preview || 'Новое сообщение';
            const ticketId = event.ticket_id;
            const go = () => void openTicket(ticketId, { fresh: true });
            maybeInAppSupportNotify({
              title: event.ticket.status === 'closed' ? 'Тикет закрыт' : 'Ответ поддержки',
              body: `${event.ticket.subject}: ${preview}`,
              ticketId,
              selectedTicketId: selectedIdRef.current,
              dedupeKey: `portal-${event.ticket.last_message_id}`,
              onToast: (msg) => {
                flashTicketRef.current = ticketId;
                setFlash(msg);
                window.setTimeout(() => setFlash(null), 4500);
              },
              onClick: go,
            });
          } else if (event.ticket.status === 'closed') {
            const ticketId = event.ticket_id;
            const go = () => void openTicket(ticketId, { fresh: true });
            maybeInAppSupportNotify({
              title: 'Тикет закрыт',
              body: event.ticket.subject,
              ticketId,
              selectedTicketId: selectedIdRef.current,
              dedupeKey: `portal-closed-${event.ticket_id}-${event.ticket.updated_at}`,
              onToast: (msg) => {
                flashTicketRef.current = ticketId;
                setFlash(msg);
                window.setTimeout(() => setFlash(null), 4500);
              },
              onClick: go,
            });
          }
          if (
            selectedIdRef.current === event.ticket_id &&
            event.ticket.status === 'closed' &&
            detailRef.current?.id === event.ticket_id &&
            detailRef.current.status !== 'closed'
          ) {
            const closedAt = event.ticket.updated_at;
            const patched = { ...detailRef.current, status: 'closed' as const, updated_at: closedAt };
            detailRef.current = patched;
            rememberDetail(patched);
            setDetail(patched);
          }
          if (
            selectedIdRef.current === event.ticket_id &&
            !showCreateRef.current &&
            event.ticket.last_message_preview &&
            event.ticket.last_message_preview !== lastPreview(detailRef.current)
          ) {
            if (detailLoadingRef.current) pendingDetailSyncRef.current = true;
            else if (!event.detail) void syncDetailFromServer(false);
          }
        }
        if (event.detail && selectedIdRef.current === event.ticket_id && !showCreateRef.current) {
          if (detailLoadingRef.current) {
            queuedWsDetailRef.current = event.detail as PortalSupportTicketDetail;
            pendingDetailSyncRef.current = true;
            return;
          }
          applyDetail(event.detail as PortalSupportTicketDetail, { forceScroll: false });
        }
      }
    },
    {
      onConnect: () => {
        window.setTimeout(() => {
          if (detailLoadingRef.current) {
            pendingDetailSyncRef.current = true;
            return;
          }
          void syncDetailFromServer(false);
        }, 250);
      },
    },
  );
  sendReceiptRef.current = sendReceipt;

  useEffect(() => {
    return () => {
      if (recordTimerRef.current != null) window.clearInterval(recordTimerRef.current);
      mediaRecorderRef.current?.stop();
    };
  }, []);

  const patchTicketPreview = (ticket: PortalSupportTicketDetail) => {
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticket.id
          ? {
              ...t,
              status: ticket.status,
              updated_at: ticket.updated_at,
              ...listMetaFromDetail(ticket),
            }
          : t,
      ),
    );
  };

  const emitActivity = useCallback(
    (state: SupportActivityState) => {
      const ticketId = selectedIdRef.current;
      if (ticketId == null) return;
      const now = Date.now();
      if (
        state === 'typing' ||
        state === 'recording_voice' ||
        state === 'selecting_emoji' ||
        state === 'selecting_sticker' ||
        state === 'selecting_gif'
      ) {
        if (lastSentActivityRef.current === state && now - typingSentAtRef.current < 1600) {
          return;
        }
        typingSentAtRef.current = now;
      } else if (lastSentActivityRef.current === state) {
        return;
      }
      lastSentActivityRef.current = state;
      sendActivity(ticketId, state);
    },
    [sendActivity],
  );

  const startLiveActivityPulse = useCallback(
    (state: SupportActivityState) => {
      clearLiveActivityPulse();
      if (typingIdleTimerRef.current != null) {
        window.clearTimeout(typingIdleTimerRef.current);
        typingIdleTimerRef.current = null;
      }
      emitActivity(state);
      liveActivityTimerRef.current = window.setInterval(() => {
        emitActivity(state);
      }, 2000);
    },
    [clearLiveActivityPulse, emitActivity],
  );

  const startRecordingActivityPulse = useCallback(() => {
    startLiveActivityPulse('recording_voice');
  }, [startLiveActivityPulse]);

  const onPickerActivityChange = useCallback(
    (state: 'selecting_emoji' | 'selecting_sticker' | 'selecting_gif' | null) => {
      if (state) {
        startLiveActivityPulse(state);
        return;
      }
      clearLiveActivityPulse();
      const last = lastSentActivityRef.current;
      if (last?.startsWith('uploading_') || busyRef.current) return;
      if (replyInputRef.current?.value.trim()) {
        emitActivity('typing');
      } else {
        emitActivity('idle');
      }
    },
    [clearLiveActivityPulse, emitActivity, startLiveActivityPulse],
  );

  const onReplyChange = (value: string) => {
    setReply(value);
    const ticketId = selectedIdRef.current;
    if (ticketId == null || detailRef.current?.status === 'closed') return;
    if (value.trim()) {
      emitActivity('typing');
      if (typingIdleTimerRef.current != null) window.clearTimeout(typingIdleTimerRef.current);
      typingIdleTimerRef.current = window.setTimeout(() => {
        typingIdleTimerRef.current = null;
        emitActivity('idle');
      }, 2500);
    } else {
      if (typingIdleTimerRef.current != null) {
        window.clearTimeout(typingIdleTimerRef.current);
        typingIdleTimerRef.current = null;
      }
      emitActivity('idle');
    }
  };

  const createTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    const media = pendingMedia;
    if (!subject.trim() || (!body.trim() && media.length === 0)) return;
    setBusy(true);
    setError(null);
    detailFetchSeq.current += 1;
    const res =
      media.length > 0
        ? await api.createSupportTicketMedia(
            subject.trim(),
            body.trim(),
            media.map((m) => m.file),
          )
        : await api.createSupportTicket(subject.trim(), body.trim());
    setBusy(false);
    if (res.success && res.data) {
      setSubject('');
      setBody('');
      clearPendingFile();
      setShowCreate(false);
      setSelectedId(res.data.id);
      selectedIdRef.current = res.data.id;
      try {
        sessionStorage.setItem(OPEN_TICKET_KEY, String(res.data.id));
      } catch {
        /* ignore */
      }
      applyDetail(res.data, { forceScroll: true, replace: true, hard: true, fromFetch: true });
      setTickets((prev) => {
        const item: PortalSupportTicketListItem = {
          id: res.data!.id,
          user_id: res.data!.user_id,
          subject: res.data!.subject,
          status: res.data!.status,
          created_at: res.data!.created_at,
          updated_at: res.data!.updated_at,
          last_message_preview: messagePreview(
            res.data!.messages.length
              ? res.data!.messages[res.data!.messages.length - 1]
              : undefined,
          ),
          last_message_author_role: lastAuthorRole(res.data!),
          last_message_id: res.data!.messages.length
            ? res.data!.messages[res.data!.messages.length - 1].id
            : null,
          last_message_type: res.data!.messages.length
            ? res.data!.messages[res.data!.messages.length - 1].message_type
            : null,
        };
        const preview = item.last_message_preview;
        if (preview) seenPreviewRef.current.set(item.id, preview);
        return sortSupportTickets([item, ...prev.filter((t) => t.id !== item.id)], ts);
      });
    } else {
      setError(res.error?.message ?? 'Не удалось создать тикет');
    }
  };

  const sendPayload = async (opts: {
    text?: string;
    file?: Blob | Blob[] | null;
    asVoice?: boolean;
    asSticker?: boolean;
    asGif?: boolean;
    asVideoNote?: boolean;
    durationMs?: number;
    filename?: string;
  }) => {
    const ticketId = selectedIdRef.current;
    const current = detailRef.current;
    if (!ticketId || !current) return;
    const text = (opts.text || '').trim();
    const files = opts.file
      ? Array.isArray(opts.file)
        ? opts.file
        : [opts.file]
      : [];
    if (!text && files.length === 0) return;

    const optimisticId = -(Date.now() * 1000 + Math.floor(Math.random() * 1000));
    const first = files[0];
    const nameHint = opts.filename || (first instanceof File ? first.name : '');
    const mime = (first && 'type' in first ? first.type : '') || '';
    const optimisticType = opts.asVideoNote
      ? 'video_note'
      : opts.asVoice
        ? 'voice'
        : opts.asSticker
          ? 'sticker'
          : opts.asGif
            ? 'gif'
            : files.length > 1
              ? 'album'
              : first
                ? mime.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(nameHint)
                  ? 'video'
                  : mime.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(nameHint)
                    ? 'image'
                    : 'voice'
                : 'text';
    const optimistic: PortalSupportMessage = {
      id: optimisticId,
      ticket_id: ticketId,
      author_telegram_id: current.user_id,
      author_role: 'user',
      message_type: optimisticType,
      body: text,
      created_at: new Date().toISOString(),
      has_attachment: files.length > 0,
      duration_ms: opts.durationMs,
      attachments:
        files.length > 1
          ? files.map((_, i) => ({ index: i }))
          : undefined,
    };
    if (first) {
      registerLocalMedia(optimisticId, first);
    }
    const optimisticDetail: PortalSupportTicketDetail = {
      ...current,
      status: current.status === 'closed' ? current.status : 'open',
      updated_at: optimistic.created_at,
      messages: [...current.messages, optimistic],
    };
    detailRef.current = optimisticDetail;
    rememberDetail(optimisticDetail);
    stickToBottomRef.current = true;
    setDetail(optimisticDetail);
    patchTicketPreview(optimisticDetail);
    setBusy(true);
    setError(null);
    detailFetchSeq.current += 1;

    if (files.length > 0) {
      emitActivity(
        activityStateFromFiles(files, {
          asVoice: opts.asVoice,
        }),
      );
    } else {
      emitActivity('idle');
    }
    if (typingIdleTimerRef.current != null) {
      window.clearTimeout(typingIdleTimerRef.current);
      typingIdleTimerRef.current = null;
    }

    const res =
      files.length > 0
        ? await api.replySupportTicketMedia(ticketId, files.length === 1 ? files[0] : files, {
            caption: text,
            asVoice: opts.asVoice,
            asSticker: opts.asSticker,
            asGif: opts.asGif,
            asVideoNote: opts.asVideoNote,
            durationMs: opts.durationMs,
            filename: opts.filename,
          })
        : await api.replySupportTicket(ticketId, text);

    setBusy(false);
    emitActivity('idle');
    if (res.success && res.data) {
      if (first) {
        const beforeMax = maxMessageId(current.messages);
        const match = findNewestMediaMessage(res.data.messages, {
          type: optimisticType === 'album' ? 'album' : optimisticType,
          size: first.size,
          afterId: beforeMax,
        });
        if (match) transferLocalMedia(optimisticId, match.id);
        else revokeLocalMedia(optimisticId);
      }
      // Bypass stale-guards: always paint server truth and drop optimistic zombies.
      const base = detailRef.current ?? current;
      const merged = mergeServerDetail(
        { ...base, messages: base.messages.filter((m) => m.id > 0) },
        res.data,
        false,
      );
      detailRef.current = merged;
      rememberDetail(merged);
      stickToBottomRef.current = true;
      lastDetailHardSyncRef.current = Date.now();
      setDetail(merged);
      patchTicketPreview(merged);
    } else {
      if (first) revokeLocalMedia(optimisticId);
      setDetail((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticId) }
          : prev,
      );
      const code = res.error?.code;
      if (code === 'file_too_large' || res.error?.message?.includes('413')) {
        setError(
          'Файл слишком большой (до 50 MB). Если в nginx уже 55m — проверьте server{} (не только location) и сделайте nginx -t && reload.',
        );
      } else {
        setError(res.error?.message ?? 'Не удалось отправить сообщение');
      }
    }
  };

  const sendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingForward.length > 0 && detail) {
      setBusy(true);
      const res = await api.forwardSupportMessages(detail.id, pendingForward.map((m) => m.id), reply);
      setBusy(false);
      if (res.success && res.data) {
        setPendingForward([]);
        setReply('');
        applyDetail(res.data, { forceScroll: true, replace: true });
      } else {
        setError(res.error?.message ?? 'Не удалось переслать сообщения');
      }
      return;
    }
    if (pendingVoice) {
      const voice = pendingVoice;
      const text = reply;
      const isCircle = voice.kind === 'circle';
      clearPendingVoice();
      setReply('');
      await sendPayload({
        text,
        file: voice.blob,
        asVoice: !isCircle,
        asVideoNote: isCircle,
        durationMs: voice.durationMs,
        filename: isCircle ? 'video-note.webm' : voiceFilenameForMime(voice.blob.type),
      });
      return;
    }
    const media = pendingMedia;
    const text = reply;
    clearPendingFile();
    setReply('');
    if (media.length === 0) {
      await sendPayload({ text, file: null });
      return;
    }
    await sendPayload({
      text,
      file: media.map((m) => m.file),
      filename: media.length === 1 ? media[0].file.name : undefined,
    });
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = filesFromFileList(e.target.files);
    e.target.value = '';
    addPendingFiles(files);
  };

  const onPasteComposer = (e: React.ClipboardEvent) => {
    const files = filesFromClipboard(e.clipboardData);
    if (!files.length) return;
    e.preventDefault();
    addPendingFiles(files);
  };

  const onDropComposer = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addPendingFiles(filesFromDataTransfer(e.dataTransfer));
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  };

  const clearRecordMaxTimer = () => {
    if (recordMaxTimerRef.current != null) {
      window.clearTimeout(recordMaxTimerRef.current);
      recordMaxTimerRef.current = null;
    }
  };

  const cancelRecording = () => {
    cancelRecordingRef.current = true;
    recordingLockedRef.current = false;
    setRecordingLocked(false);
    setCaptureGesture(null);
    setCircleLiveStream(null);
    clearRecordMaxTimer();
    stopRecording();
  };

  const startRecording = async (mode: 'voice' | 'circle' = captureModeRef.current) => {
    if (recording || busy) return;
    setError(null);
    clearPendingFile();
    clearPendingVoice();
    const isCircle = mode === 'circle';
    try {
      let stream: MediaStream;
      if (isCircle) {
        const wantAudio = true;
        const circleVideoBase: MediaTrackConstraints = {
          width: { ideal: 480, max: 640 },
          height: { ideal: 480, max: 640 },
          frameRate: { ideal: 24, max: 30 },
        };
        const attempts: MediaStreamConstraints[] = [];
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const back = devices.find(
            (d) =>
              d.kind === 'videoinput' &&
              /back|rear|environment|задн|основн/i.test(d.label || ''),
          );
          if (back?.deviceId) {
            attempts.push({
              video: { ...circleVideoBase, deviceId: { exact: back.deviceId } },
              audio: wantAudio,
            });
            attempts.push({
              video: { ...circleVideoBase, deviceId: { exact: back.deviceId } },
              audio: false,
            });
          }
        } catch {
          /* ignore enumerate errors */
        }
        attempts.push(
          {
            video: { ...circleVideoBase, facingMode: { exact: 'environment' } },
            audio: wantAudio,
          },
          {
            video: { ...circleVideoBase, facingMode: { ideal: 'environment' } },
            audio: wantAudio,
          },
          {
            video: { ...circleVideoBase, facingMode: { exact: 'environment' } },
            audio: false,
          },
          {
            video: { ...circleVideoBase, facingMode: { ideal: 'environment' } },
            audio: false,
          },
        );
        let lastErr: unknown = null;
        let acquired: MediaStream | null = null;
        for (const constraints of attempts) {
          try {
            acquired = await navigator.mediaDevices.getUserMedia(constraints);
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
          }
        }
        if (!acquired) {
          throw lastErr || new Error('back_camera_required');
        }
        if (circleMutedRef.current) {
          acquired.getAudioTracks().forEach((t) => {
            t.enabled = false;
          });
        }
        stream = acquired;
        setCircleLiveStream(acquired);
      } else {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setCircleLiveStream(null);
      }
      recordStreamRef.current = stream;
      const mime = isCircle
        ? MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : MediaRecorder.isTypeSupported('video/webm')
            ? 'video/webm'
            : MediaRecorder.isTypeSupported('video/mp4')
              ? 'video/mp4'
              : ''
        : pickVoiceRecorderMime();
      let recorder: MediaRecorder;
      try {
        const recorderOpts: MediaRecorderOptions = {};
        if (mime) recorderOpts.mimeType = mime;
        if (isCircle) {
          recorderOpts.videoBitsPerSecond = 750_000;
          recorderOpts.audioBitsPerSecond = 64_000;
        }
        recorder =
          Object.keys(recorderOpts).length > 0
            ? new MediaRecorder(stream, recorderOpts)
            : new MediaRecorder(stream);
      } catch {
        stream.getTracks().forEach((t) => t.stop());
        recordStreamRef.current = null;
        setCircleLiveStream(null);
        setError(
          isCircle
            ? 'Браузер не умеет записывать видеосообщения. Попробуйте Chrome/Edge.'
            : 'Браузер не умеет записывать голос',
        );
        return;
      }
      recordChunksRef.current = [];
      recordStartedAt.current = Date.now();
      cancelRecordingRef.current = false;
      setRecordMs(0);
      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) recordChunksRef.current.push(ev.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        recordStreamRef.current = null;
        setCircleLiveStream(null);
        if (recordTimerRef.current != null) {
          window.clearInterval(recordTimerRef.current);
          recordTimerRef.current = null;
        }
        clearRecordMaxTimer();
        clearRecordingActivityPulse();
        emitActivity('idle');
        setRecording(false);
        recordingLockedRef.current = false;
        setRecordingLocked(false);
        setCaptureGesture(null);
        const cancelled = cancelRecordingRef.current;
        cancelRecordingRef.current = false;
        mediaRecorderRef.current = null;
        if (cancelled) {
          recordChunksRef.current = [];
          return;
        }
        const durationMs = Date.now() - recordStartedAt.current;
        const blobType = recorder.mimeType || mime || (isCircle ? 'video/webm' : 'audio/webm');
        const blob = new Blob(recordChunksRef.current, {
          type: blobType,
        });
        recordChunksRef.current = [];
        if (blob.size < 400) {
          setError(isCircle ? 'Слишком короткое видеосообщение' : 'Слишком короткое голосовое');
          return;
        }
        setPendingVoice({
          blob,
          durationMs,
          url: URL.createObjectURL(blob),
          kind: isCircle ? 'circle' : 'voice',
        });
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecording(true);
      startRecordingActivityPulse();
      recordTimerRef.current = window.setInterval(() => {
        setRecordMs(Date.now() - recordStartedAt.current);
      }, 200);
      if (isCircle) {
        recordingLockedRef.current = true;
        setRecordingLocked(true);
        clearRecordMaxTimer();
        recordMaxTimerRef.current = window.setTimeout(() => {
          stopRecording();
        }, 60_000);
      }
    } catch {
      clearRecordingActivityPulse();
      clearRecordMaxTimer();
      recordStreamRef.current = null;
      setCircleLiveStream(null);
      setCaptureGesture(null);
      setError(
        isCircle
          ? 'Нет доступа к задней камере. Фронтальная камера для кружков не используется.'
          : 'Нет доступа к микрофону',
      );
      setRecording(false);
      recordingLockedRef.current = false;
      setRecordingLocked(false);
    }
  };

  const beginCapturePress = async (e: React.PointerEvent) => {
    if (e.button !== 0 || recording || busy) return;
    e.preventDefault();
    const startY = e.clientY;
    const startX = e.clientX;
    const btn = e.currentTarget.getBoundingClientRect();
    const originX = btn.left + btn.width / 2;
    const originY = btn.top + btn.height / 2;
    const lockTravel = 56;
    const startedAt = Date.now();
    let recordingStarted = false;
    let maxMove = 0;
    circleHoldActiveRef.current = true;
    recordingLockedRef.current = false;
    setRecordingLocked(false);
    // Do not show swipe UI until the pointer actually moves up.

    const holdTimer = window.setTimeout(() => {
      if (!circleHoldActiveRef.current) return;
      recordingStarted = true;
      void (async () => {
        await startRecording(captureModeRef.current);
        if (!circleHoldActiveRef.current && !recordingLockedRef.current) {
          if (mediaRecorderRef.current) stopRecording();
          else cancelRecordingRef.current = true;
        }
      })();
    }, 160);

    const onMove = (ev: PointerEvent) => {
      maxMove = Math.max(maxMove, Math.hypot(ev.clientX - startX, ev.clientY - startY));
      const lift = Math.max(0, Math.min(lockTravel, startY - ev.clientY));
      const lockProgress = Math.min(1, lift / lockTravel);
      if (lift >= lockTravel && recordingStarted && !recordingLockedRef.current) {
        recordingLockedRef.current = true;
        setRecordingLocked(true);
      }
      // Swipe indicator only after real upward travel (not on tap).
      if (lift >= 10) {
        setCaptureGesture({
          originX,
          originY,
          lift,
          lockProgress,
          locked: recordingLockedRef.current,
          mode: captureModeRef.current,
        });
      } else {
        setCaptureGesture(null);
      }
    };
    const onUp = () => {
      cleanup();
      const elapsed = Date.now() - startedAt;
      circleHoldActiveRef.current = false;
      setCaptureGesture(null);
      if (!recordingStarted && elapsed < 280 && maxMove < 12) {
        setCaptureMode((m) => (m === 'voice' ? 'circle' : 'voice'));
        return;
      }
      if (!recordingLockedRef.current) {
        if (mediaRecorderRef.current) stopRecording();
        else cancelRecordingRef.current = true;
      }
    };
    const cleanup = () => {
      window.clearTimeout(holdTimer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const toggleCircleMute = () => {
    setCircleMuted((prev) => {
      const next = !prev;
      const stream = recordStreamRef.current;
      if (stream) {
        const tracks = stream.getAudioTracks();
        if (tracks.length) {
          tracks.forEach((t) => {
            t.enabled = !next;
          });
        }
      }
      circleMutedRef.current = next;
      return next;
    });
  };

  const runMessageAction = async (
    action: 'edit' | 'delete' | 'forward' | 'select' | 'copy',
    message: PortalSupportMessage,
  ) => {
    setMenu(null);
    if (action === 'copy') {
      const text = (message.body || '').trim();
      if (!text) {
        setError('В сообщении нет текста для копирования');
        return;
      }
      const { copyText } = await import('../utils/copyText');
      const ok = await copyText(text);
      if (!ok) setError('Не удалось скопировать текст');
      return;
    }
    if (action === 'select') {
      setSelectedIds((prev) => (prev.includes(message.id) ? prev : [...prev, message.id]));
      return;
    }
    if (action === 'edit') {
      if ((message.message_type || 'text') !== 'text' || isForwardedMessage(message)) return;
      setEditingId(message.id);
      setEditText(message.body);
      return;
    }
    if (action === 'forward') {
      setPendingForward((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      return;
    }
    setBusy(true);
    const res = await api.deleteSupportMessage(message.id);
    setBusy(false);
    if (res.success && res.data) {
      applyDetail(res.data, { replace: true });
    } else if (action === 'delete' && res.error?.code === 'message_not_found') {
      // Already gone (double-click / race) — refresh thread quietly.
      void syncDetailFromServer(false);
    } else {
      setError(res.error?.message ?? 'Не удалось выполнить действие');
    }
  };

  const toggleReaction = useCallback(
    async (messageId: number, emoji: string) => {
      const ticketId = selectedIdRef.current;
      if (!ticketId || !emoji || messageId <= 0) return;
      const msg = detailRef.current?.messages.find((m) => m.id === messageId);
      const alreadyMine = Boolean(msg?.reactions?.some((r) => r.emoji === emoji && r.me));
      if (!alreadyMine) setLastReaction(emoji);
      const res = await api.setSupportReaction(ticketId, messageId, emoji);
      if (res.success && res.data) applyDetail(res.data, { forceScroll: false, replace: true });
      else if (res.error) setError(res.error.message ?? 'Не удалось поставить реакцию');
    },
    [applyDetail],
  );

  const toggleSelected = useCallback((id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const forwardSelected = useCallback(() => {
    if (!detail || selectedIds.length === 0) return;
    setPendingForward(detail.messages.filter((m) => selectedIds.includes(m.id)));
    setSelectedIds([]);
  }, [detail, selectedIds]);

  const deleteSelected = useCallback(async () => {
    if (!detail || selectedIds.length === 0) return;
    const mineIds = detail.messages
      .filter(
        (m) =>
          selectedIds.includes(m.id) &&
          m.author_role === 'user' &&
          user?.telegram_user_id != null &&
          Number(user.telegram_user_id) === Number(m.author_telegram_id),
      )
      .map((m) => m.id);
    if (!mineIds.length) {
      setError('Можно удалить только свои сообщения');
      return;
    }
    setBusy(true);
    setError(null);
    let lastDetail = detail;
    for (const id of mineIds) {
      const res = await api.deleteSupportMessage(id);
      if (res.success && res.data) lastDetail = res.data;
      else if (res.error?.code !== 'message_not_found') {
        setError(res.error?.message ?? 'Не удалось удалить');
        break;
      }
    }
    setBusy(false);
    setSelectedIds([]);
    applyDetail(lastDetail, { replace: true });
  }, [detail, selectedIds, user?.telegram_user_id]);

  const insertEmoji = useCallback((emoji: string) => {
    const el = replyInputRef.current;
    if (!el) {
      setReply((prev) => prev + emoji);
      return;
    }
    const start = el.selectionStart ?? reply.length;
    const end = el.selectionEnd ?? reply.length;
    const next = reply.slice(0, start) + emoji + reply.slice(end);
    setReply(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  }, [reply]);

  const saveEdit = async () => {
    if (editingId == null) return;
    const text = editText.trim();
    if (!text) return;
    setBusy(true);
    const res = await api.editSupportMessage(editingId, text);
    setBusy(false);
    if (res.success && res.data) {
      setEditingId(null);
      setEditText('');
      applyDetail(res.data, { forceScroll: false, replace: true });
    } else {
      setError(res.error?.message ?? 'Не удалось изменить сообщение');
    }
  };

  const closeTicket = async () => {
    if (!selectedId) return;
    setBusy(true);
    detailFetchSeq.current += 1;
    const res = await api.closeSupportTicket(selectedId);
    setBusy(false);
    if (res.success && res.data) {
      applyDetail(res.data, { replace: true });
      setTickets((prev) =>
        prev.map((t) =>
          t.id === res.data!.id
            ? { ...t, status: res.data!.status, updated_at: res.data!.updated_at }
            : t,
        ),
      );
    } else {
      setError(res.error?.message ?? 'Не удалось закрыть тикет');
    }
  };

  const exitThread = useCallback(() => {
    const id = selectedIdRef.current;
    if (id != null) {
      sendActivity(id, 'idle');
      detailCacheRef.current.delete(id);
    }
    setSelectedId(null);
    selectedIdRef.current = null;
    setDetail(null);
    setSelectedIds([]);
    setEmojiOpen(false);
    setPeerActivity(null);
    detailRef.current = null;
    setShowCreate(false);
    clearRecordingActivityPulse();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      cancelRecordingRef.current = true;
      mediaRecorderRef.current.stop();
    }
    clearPendingFile();
    clearPendingVoice();
    setReply('');
    setError(null);
    lastSentActivityRef.current = null;
    if (typingIdleTimerRef.current != null) {
      window.clearTimeout(typingIdleTimerRef.current);
      typingIdleTimerRef.current = null;
    }
    try {
      sessionStorage.removeItem(OPEN_TICKET_KEY);
    } catch {
      /* ignore */
    }
  }, [clearPendingFile, clearPendingVoice, clearRecordingActivityPulse, sendActivity]);

  const threadOpen = showCreate || detail != null;

  useEffect(() => {
    document.body.classList.toggle('support-recording', recording);
    return () => {
      document.body.classList.remove('support-recording');
    };
  }, [recording]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    const sync = () => {
      document.body.classList.toggle('support-chat-open', threadOpen && mq.matches);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      document.body.classList.remove('support-chat-open');
      mq.removeEventListener('change', sync);
    };
  }, [threadOpen]);

  return (
    <div className={`support-section support-v3${threadOpen ? ' has-thread' : ''}`}>
      {flash && !threadOpen && (
        <p
          className="support-flash"
          role="status"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            const id = flashTicketRef.current;
            setFlash(null);
            if (id != null) void openTicket(id, { fresh: true });
          }}
        >
          {flash}
        </p>
      )}
      {error && !threadOpen && <p className="error-text">{error}</p>}

      <div className="support-layout">
        <div className="card section-block support-list">
          <div className="support-list-head">
            <h3>Мои тикеты</h3>
            <div className="support-list-actions">
              <button
                type="button"
                className="btn btn-ghost support-btn-refresh"
                onClick={() => void loadList()}
                disabled={loading}
                title="Обновить"
              >
                Обновить
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowCreate(true);
                  setSelectedId(null);
                  selectedIdRef.current = null;
                  setDetail(null);
                  detailRef.current = null;
                  setPeerActivity(null);
                  clearPendingFile();
                  clearPendingVoice();
                }}
              >
                Новый тикет
              </button>
            </div>
          </div>
          <div className="support-list-push">
            <SupportPushControls
              onMessage={(type, text) => {
                if (type === 'err') setError(text);
                else {
                  setError(null);
                  setFlash(text);
                  window.setTimeout(() => setFlash(null), 3500);
                }
              }}
            />
          </div>
          {loading ? (
            <p className="muted">Загрузка…</p>
          ) : tickets.length === 0 ? (
            <p className="muted">Пока нет тикетов</p>
          ) : (
            <ul className="support-ticket-list">
              {tickets.map((t) => {
                const unread =
                  t.last_message_author_role === 'support' &&
                  Boolean(t.last_message_preview) &&
                  t.last_message_preview !== seenPreviewRef.current.get(t.id) &&
                  selectedId !== t.id;
                return (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`support-ticket-item ${selectedId === t.id ? 'active' : ''}${unread ? ' is-unread' : ''}`}
                    onClick={() => void openTicket(t.id)}
                  >
                    <span className="support-ticket-top">
                      <span className="support-ticket-subject">{t.subject}</span>
                      <span className="support-ticket-time">{ticketListTime(t.updated_at)}</span>
                    </span>
                    <span className="support-ticket-bottom">
                      <span className="support-ticket-preview-row">
                        {t.last_message_id &&
                        (t.last_message_type === 'image' ||
                          t.last_message_type === 'video' ||
                          t.last_message_type === 'album' ||
                          t.last_message_type === 'sticker' ||
                          t.last_message_type === 'gif') ? (
                          <span className="support-ticket-thumbs" aria-hidden>
                            {Array.from({
                              length: Math.min(
                                3,
                                Math.max(
                                  1,
                                  t.last_message_type === 'album'
                                    ? Number(t.last_message_media_count) || 1
                                    : 1,
                                ),
                              ),
                            }).map((_, i) => (
                              <img
                                key={i}
                                className="support-ticket-thumb"
                                src={api.supportMediaUrl(
                                  t.last_message_id!,
                                  `${t.last_message_id}-${t.updated_at}-${i}`,
                                  i,
                                )}
                                alt=""
                              />
                            ))}
                          </span>
                        ) : null}
                        <span className="support-ticket-preview">
                          {t.last_message_preview || 'Нет сообщений'}
                        </span>
                      </span>
                      <span
                        className={`support-ticket-status ${
                          statusOrActivityLabel(t.status, ticketActivity[t.id], statusLabel).isActivity
                            ? 'is-activity'
                            : statusClass(t.status)
                        }`}
                      >
                        {statusOrActivityLabel(t.status, ticketActivity[t.id], statusLabel).text}
                      </span>
                    </span>
                    {unread ? <span className="support-ticket-unread-dot" aria-hidden /> : null}
                  </button>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="card section-block support-thread">
          {showCreate ? (
            <>
              <div className="support-thread-header">
                <div className="support-thread-heading">
                  <button type="button" className="support-back-btn" onClick={exitThread}>
                    ← Назад
                  </button>
                  <h3 style={{ margin: 0 }}>Новый тикет</h3>
                </div>
              </div>
              {error && <p className="error-text">{error}</p>}
              <form
                onSubmit={createTicket}
                className={`support-form${dragOver ? ' is-dragover' : ''}`}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                  setDragOver(false);
                }}
                onDrop={onDropComposer}
              >
                <label className="field" htmlFor="portal-support-subject">
                  <span>Тема</span>
                  <input
                    id="portal-support-subject"
                    name="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    required
                    placeholder="Кратко о проблеме"
                    autoComplete="off"
                  />
                </label>
                <label className="field" htmlFor="portal-support-body">
                  <span>Сообщение</span>
                  <textarea
                    id="portal-support-body"
                    name="body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={4000}
                    rows={6}
                    required={pendingMedia.length === 0}
                    placeholder="Опишите проблему…"
                    onPaste={onPasteComposer}
                  />
                </label>
                {pendingMedia.length > 0 && (
                  <div className="support-pending-list">
                    {pendingMedia.map((item, idx) => (
                      <div key={`${item.file.name}-${idx}`} className="support-pending-file">
                        {item.kind === 'video' && item.previewUrl ? (
                          <div className="support-pending-video">
                            <video
                              src={item.previewUrl}
                              controls
                              playsInline
                              preload="metadata"
                              aria-label={item.file.name}
                            />
                          </div>
                        ) : item.previewUrl ? (
                          <button
                            type="button"
                            className="support-pending-thumb"
                            onClick={() => openLightbox(item.previewUrl!, item.file.name)}
                          >
                            <img src={item.previewUrl} alt="Превью" />
                          </button>
                        ) : (
                          <span className="support-pending-name">{item.file.name}</span>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() =>
                            setPendingMedia((prev) => {
                              const next = [...prev];
                              const [removed] = next.splice(idx, 1);
                              if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
                              return next;
                            })
                          }
                        >
                          Убрать
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  id="portal-support-create-attach"
                  name="support_create_attachments"
                  ref={createFileInputRef}
                  type="file"
                  accept="image/*,video/mp4,video/webm,video/quicktime,.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm,.mov"
                  multiple
                  hidden
                  onChange={onPickFile}
                />
                <div className="support-form-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => createFileInputRef.current?.click()}
                  >
                    Фото / видео
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={busy || !subject.trim() || (!body.trim() && pendingMedia.length === 0)}
                  >
                    {busy ? 'Отправка…' : 'Создать'}
                  </button>
                </div>
              </form>
            </>
          ) : detail ? (
            <>
              <div className="support-thread-header">
                {selectedIds.length > 0 ? (
                  <SelectionBar
                    count={selectedIds.length}
                    canDelete={detail.messages.some(
                      (m) =>
                        selectedIds.includes(m.id) &&
                        m.author_role === 'user' &&
                        user?.telegram_user_id != null &&
                        Number(user.telegram_user_id) === Number(m.author_telegram_id),
                    )}
                    onForward={forwardSelected}
                    onDelete={() => void deleteSelected()}
                    onCancel={() => setSelectedIds([])}
                  />
                ) : (
                  <>
                    <div className="support-thread-heading">
                      <button type="button" className="support-back-btn" onClick={exitThread}>
                        ← Назад
                      </button>
                      <div>
                        <h3 style={{ margin: 0 }}>{detail.subject}</h3>
                        <p className="muted" style={{ margin: '0.35rem 0 0', fontSize: '0.875rem' }}>
                          #{detail.id} ·{' '}
                          {(() => {
                            const live = statusOrActivityLabel(
                              detail.status,
                              ticketActivity[detail.id] ?? peerActivity,
                              statusLabel,
                            );
                            return (
                              <span
                                className={
                                  live.isActivity ? 'support-status-activity' : statusClass(detail.status)
                                }
                              >
                                {live.text}
                              </span>
                            );
                          })()}
                        </p>
                      </div>
                    </div>
                    {detail.status !== 'closed' && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={busy}
                        onClick={() => void closeTicket()}
                      >
                        Закрыть
                      </button>
                    )}
                  </>
                )}
              </div>
              {error && <p className="error-text">{error}</p>}
              <div
                className="support-messages"
                ref={messagesRef}
                onScroll={() => longPressCancelRef.current?.()}
                onTouchMove={() => longPressCancelRef.current?.()}
              >
                <div className="support-messages-inner" ref={messagesInnerRef}>
                {buildMessageRows(detail.messages).map((row) => {
                  if (row.type === 'day') {
                    return (
                      <div key={row.key} className="support-day-sep">
                        <span>{row.label}</span>
                      </div>
                    );
                  }
                  const m = row.message;
                  const isUser = m.author_role === 'user';
                  // Must match author — never treat foreign user messages as mine.
                  const mine =
                    isUser &&
                    user?.telegram_user_id != null &&
                    Number(user.telegram_user_id) === Number(m.author_telegram_id);
                  const canEdit = Boolean(
                    mine &&
                      !isForwardedMessage(m) &&
                      (m.message_type || 'text') === 'text' &&
                      detail.status !== 'closed',
                  );
                  const canDelete = Boolean(mine && detail.status !== 'closed');
                  const canForward = detail.status !== 'closed' && m.id > 0;
                  const selectMode = selectedIds.length > 0;
                  const picked = selectedIds.includes(m.id);
                  return (
                    <div
                      key={row.key}
                      data-message-id={m.id}
                      className={`support-msg ${mine ? 'is-mine' : 'is-support'}${selectMode ? ' is-selecting' : ''}${picked ? ' is-picked' : ''}${highlightMessageId === m.id ? ' is-highlight' : ''}`}
                      onClick={() => {
                        if (selectMode && m.id > 0) {
                          toggleSelected(m.id);
                          return;
                        }
                        if (detail.status === 'closed' || m.id <= 0 || busy) return;
                        const now = Date.now();
                        const prev = lastTapRef.current;
                        if (prev && prev.id === m.id && now - prev.at < 340) {
                          lastTapRef.current = null;
                          const emoji = getLastReaction() || REACTION_QUICK_EMOJIS[0];
                          const alreadyMine = Boolean(m.reactions?.some((r) => r.emoji === emoji && r.me));
                          if (!alreadyMine) void toggleReaction(m.id, emoji);
                          return;
                        }
                        lastTapRef.current = { id: m.id, at: now };
                      }}
                      onContextMenu={(e) => {
                        if (selectMode) return;
                        if (!canEdit && !canDelete && !canForward) return;
                        e.preventDefault();
                        setMenu({ message: m, x: e.clientX, y: e.clientY });
                      }}
                      onPointerDown={(e) => {
                        if (selectMode) return;
                        if (!canEdit && !canDelete && !canForward) return;
                        bindMessageLongPress(
                          e,
                          (x, y) => setMenu({ message: m, x, y }),
                          longPressCancelRef,
                        );
                      }}
                    >
                      {!mine && selectMode ? <SelectionCheck on={picked} /> : null}
                      <div className={`support-bubble ${mine ? 'is-mine' : 'is-support'}`}>
                        {editingId === m.id ? (
                          <div className="support-edit-box">
                            <textarea
                              id={`portal-support-edit-${m.id}`}
                              name={`support_edit_${m.id}`}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              rows={3}
                              maxLength={4000}
                              aria-label="Редактирование сообщения"
                            />
                            <div className="support-edit-actions">
                              <button type="button" className="btn btn-ghost" onClick={() => setEditingId(null)}>
                                Отмена
                              </button>
                              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void saveEdit()}>
                                Сохранить
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {!mine && m.author_display_name ? (
                              <div className="support-msg-author">{m.author_display_name}</div>
                            ) : null}
                            {isForwardedMessage(m) ? <ForwardedLabel /> : null}
                            {m.forwarded_bundle?.length ? (
                              <>
                                <div className="support-forward-nest">
                                  {m.forwarded_bundle.map((item) => {
                                    const mediaType = item.message_type || 'text';
                                    const thumb =
                                      item.media_count > 0 &&
                                      (mediaType === 'image' ||
                                        mediaType === 'gif' ||
                                        mediaType === 'sticker' ||
                                        mediaType === 'video' ||
                                        mediaType === 'album')
                                        ? api.supportMediaUrl(
                                            m.id,
                                            `${m.id}-${item.from_message_id}-${item.media_index}`,
                                            item.media_index,
                                          )
                                        : null;
                                    return (
                                      <div key={item.from_message_id} className="support-forward-nest-item">
                                        <ForwardQuotePreview
                                          author={forwardAuthorLabel(item.author_role, {
                                            username: user?.username,
                                            firstName: user?.first_name,
                                            selfIsUser: item.author_role === 'user',
                                            supportDisplayName:
                                              item.author_role === 'support'
                                                ? m.author_display_name
                                                : null,
                                          })}
                                          snippet={forwardSnippet(item)}
                                          thumbUrl={thumb}
                                          onOpen={() => focusOriginalMessage(item.from_message_id)}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                                {m.body.trim() ? <div className="support-bubble-text">{m.body}</div> : null}
                                {!m.body.trim() &&
                                (['image', 'video', 'sticker', 'gif', 'voice', 'video_note', 'album'].includes(
                                  m.message_type || '',
                                ) ||
                                  m.has_attachment) ? (
                                  <MessageBody
                                    message={{
                                      ...m,
                                      forwarded_from_message_id: null,
                                      forwarded_bundle: [],
                                    }}
                                    onOpenImage={selectMode ? () => undefined : openLightbox}
                                    onOpenGallery={selectMode ? () => undefined : openLightboxGallery}
                                    mediaAlign={mine ? 'end' : 'start'}
                                    onMediaLayout={onMessageMediaReady}
                                  />
                                ) : null}
                              </>
                            ) : (
                              <>
                                {isForwardedMessage(m) && m.forwarded_from_message_id ? (
                                  <ForwardQuotePreview
                                    author={forwardAuthorLabel(
                                      detail.messages.find((x) => x.id === m.forwarded_from_message_id)
                                        ?.author_role,
                                      {
                                        username: user?.username,
                                        firstName: user?.first_name,
                                        selfIsUser: true,
                                      },
                                    )}
                                    snippet={forwardSnippet(
                                      detail.messages.find((x) => x.id === m.forwarded_from_message_id) ||
                                        m,
                                    )}
                                    onOpen={() => focusOriginalMessage(Number(m.forwarded_from_message_id))}
                                  />
                                ) : null}
                                <MessageBody
                                  message={m}
                                  onOpenImage={selectMode ? () => undefined : openLightbox}
                                  onOpenGallery={selectMode ? () => undefined : openLightboxGallery}
                                  mediaAlign={mine ? 'end' : 'start'}
                                  onMediaLayout={onMessageMediaReady}
                                />
                              </>
                            )}
                            <MessageTime
                              createdAt={m.created_at}
                              editedAt={m.edited_at}
                              receipt={mine ? messageReceiptStatus(m) : null}
                              reactions={m.reactions}
                              myTelegramId={
                                user?.telegram_user_id != null ? Number(user.telegram_user_id) : null
                              }
                              reactionsDisabled={detail.status === 'closed' || m.id <= 0 || busy}
                              onToggleReaction={(emoji) => void toggleReaction(m.id, emoji)}
                            />
                          </>
                        )}
                      </div>
                      {mine && selectMode ? <SelectionCheck on={picked} /> : null}
                    </div>
                  );
                })}
                {activityLabel(peerActivity) ? (
                  <div className="support-activity" aria-live="polite">
                    {activityLabel(peerActivity)}
                  </div>
                ) : null}
                </div>
              </div>
              {detail.status !== 'closed' ? (
                <form
                  onSubmit={sendReply}
                  className={`support-composer${dragOver ? ' is-dragover' : ''}`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                    setDragOver(false);
                  }}
                  onDrop={onDropComposer}
                >
                  {pendingMedia.length > 0 && (
                    <div className="support-pending-list">
                      {pendingMedia.map((item, idx) => (
                        <div key={`${item.file.name}-${idx}`} className="support-pending-file">
                          {item.kind === 'video' && item.previewUrl ? (
                            <div className="support-pending-video">
                              <video
                                src={item.previewUrl}
                                controls
                                playsInline
                                preload="metadata"
                                aria-label={item.file.name}
                              />
                            </div>
                          ) : item.previewUrl ? (
                            <button
                              type="button"
                              className="support-pending-thumb"
                              onClick={() => openLightbox(item.previewUrl!, item.file.name)}
                            >
                              <img src={item.previewUrl} alt="Превью" />
                            </button>
                          ) : (
                            <span className="support-pending-name">{item.file.name}</span>
                          )}
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() =>
                              setPendingMedia((prev) => {
                                const next = [...prev];
                                const [removed] = next.splice(idx, 1);
                                if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
                                return next;
                              })
                            }
                          >
                            Убрать
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {pendingForward.length > 0 && (
                    <div className="support-forward-draft">
                      <span>Пересылается: {pendingForward.length}</span>
                      <button type="button" className="btn btn-ghost" onClick={() => setPendingForward([])}>Убрать</button>
                    </div>
                  )}
                  {pendingVoice && !recording && (
                    <div className="support-pending-voice">
                      {pendingVoice.kind === 'circle' ? (
                        <VideoNotePlayer src={pendingVoice.url} durationMs={pendingVoice.durationMs} />
                      ) : (
                        <VoicePlayer src={pendingVoice.url} durationMs={pendingVoice.durationMs} />
                      )}
                      <button type="button" className="btn btn-ghost" onClick={clearPendingVoice}>
                        Убрать
                      </button>
                    </div>
                  )}
                  {recording ? (
                    <div className="support-recording-locked">
                      <div className="support-recording-timer">
                        {formatDuration(recordMs)}
                        {captureMode === 'circle'
                          ? ' · запись кружка'
                          : !recordingLocked
                            ? ' · вверх — закрепить'
                            : ' · запись'}
                      </div>
                      {captureMode === 'circle' ? (
                        <button
                          type="button"
                          className={`support-icon-btn${circleMuted ? ' is-active' : ''}`}
                          title={circleMuted ? 'Включить микрофон' : 'Выключить микрофон'}
                          onClick={toggleCircleMute}
                        >
                          {circleMuted ? <IconMicOff /> : <IconMic />}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="support-icon-btn is-danger"
                        title="Отменить"
                        onClick={cancelRecording}
                      >
                        <IconTrash />
                      </button>
                      <button
                        type="button"
                        className="support-record-finish-btn"
                        title="Готово"
                        onClick={stopRecording}
                      >
                        Готово
                      </button>
                    </div>
                  ) : null}
                  <input
                    id="portal-support-attach"
                    name="support_attachments"
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/mp4,video/webm,video/quicktime,.jpg,.jpeg,.png,.gif,.webp,.mp4,.webm,.mov"
                    multiple
                    hidden
                    onChange={onPickFile}
                  />
                  {!recording && (
                    <div className="support-composer-stack">
                      <EmojiPickerPanel
                        open={emojiOpen}
                        onClose={() => setEmojiOpen(false)}
                        onActivityChange={onPickerActivityChange}
                        onPickEmoji={(emoji) => insertEmoji(emoji)}
                        onPickStickerFile={(file) => {
                          void sendPayload({ text: '', file, asSticker: true, filename: file.name });
                        }}
                        onPickGifFile={(file) => {
                          void sendPayload({ text: '', file, asGif: true, filename: file.name });
                        }}
                      />
                      <div className="support-input-shell">
                        <AttachMenu disabled={busy} onPickMedia={() => fileInputRef.current?.click()} />
                        <textarea
                          id="portal-support-reply"
                          name="support_reply"
                          ref={replyInputRef}
                          value={reply}
                          onChange={(e) => onReplyChange(e.target.value)}
                          maxLength={4000}
                          rows={1}
                          placeholder="Сообщение..."
                          aria-label="Сообщение"
                          onPaste={onPasteComposer}
                          onFocus={() => setEmojiOpen(false)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (!busy && (reply.trim() || pendingMedia.length > 0 || pendingVoice || pendingForward.length > 0)) {
                                e.currentTarget.form?.requestSubmit();
                              }
                            }
                          }}
                        />
                        <div className="support-input-tools">
                          {!reply.trim() &&
                          pendingMedia.length === 0 &&
                          !pendingVoice &&
                          pendingForward.length === 0 &&
                          captureMode === 'circle' ? (
                            <button
                              type="button"
                              className={`support-icon-btn${circleMuted ? ' is-active' : ''}`}
                              title={circleMuted ? 'Микрофон выкл — нажмите чтобы включить' : 'Микрофон вкл'}
                              disabled={busy}
                              onClick={toggleCircleMute}
                            >
                              {circleMuted ? <IconMicOff /> : <IconMic />}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={`support-icon-btn${emojiOpen ? ' is-active' : ''}`}
                            title="Эмодзи"
                            data-emoji-toggle
                            disabled={busy}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => setEmojiOpen((v) => !v)}
                          >
                            <IconSmile />
                          </button>
                          {reply.trim() || pendingMedia.length > 0 || pendingVoice || pendingForward.length > 0 ? (
                            <button
                              type="submit"
                              className="support-icon-btn is-send"
                              title="Отправить"
                              disabled={busy}
                            >
                              <IconSend />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="support-icon-btn"
                              title={
                                captureMode === 'circle'
                                  ? 'Тап — микрофон · удерживайте — кружок · вверх — закрепить'
                                  : 'Тап — кружок · удерживайте — голос · вверх — закрепить'
                              }
                              disabled={busy}
                              onPointerDown={(e) => void beginCapturePress(e)}
                            >
                              {captureMode === 'circle' ? <IconCircle /> : <IconMic />}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              ) : (
                <ClosedTicketBanner
                  variant="portal"
                  onCreateTicket={() => {
                    setShowCreate(true);
                    setSelectedId(null);
                    selectedIdRef.current = null;
                    setDetail(null);
                    detailRef.current = null;
                    setPeerActivity(null);
                    clearPendingFile();
                    clearPendingVoice();
                  }}
                />
              )}
            </>
          ) : (
            <p className="muted support-thread-placeholder" style={{ margin: 0 }}>
              Выберите тикет или создайте новый
            </p>
          )}
          {recording && captureMode === 'circle' && circleLiveStream ? (
            <div className="support-circle-record-overlay">
              <div className="support-circle-record-stage">
                <p className="support-circle-record-timer">{formatDuration(recordMs)}</p>
                <CircleLivePreview stream={circleLiveStream} variant="overlay" />
                <p className="support-circle-record-hint">Нажмите «Готово», чтобы закончить запись</p>
                <div className="support-circle-record-actions">
                  <button
                    type="button"
                    className="support-circle-record-cancel"
                    onClick={cancelRecording}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="support-circle-record-finish"
                    onClick={stopRecording}
                  >
                    Готово
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {captureGesture ? (
        <div className="support-capture-gesture-layer" aria-hidden>
          <div
            className="support-capture-gesture-track"
            style={{
              left: captureGesture.originX,
              top: captureGesture.originY,
              height: 56 + 36,
            }}
          >
            <div
              className={`support-capture-gesture-lock${captureGesture.locked ? ' is-locked' : ''}${
                captureGesture.lockProgress > 0.15 ? ' is-visible' : ''
              }`}
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                {captureGesture.locked ? (
                  <path
                    fill="currentColor"
                    d="M9.5 16.2 5.8 12.5l1.4-1.4 2.3 2.3 5.3-5.3 1.4 1.4z"
                  />
                ) : (
                  <path
                    fill="currentColor"
                    d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Zm-7-2a2 2 0 1 1 4 0v2h-4Zm7 12H7v-8h10Z"
                  />
                )}
              </svg>
            </div>
          </div>
          <div
            className={`support-capture-gesture${captureGesture.locked ? ' is-locked' : ''}`}
            style={{
              left: captureGesture.originX,
              top: captureGesture.originY - captureGesture.lift,
              ['--lock-progress' as string]: String(captureGesture.lockProgress),
            }}
          >
            <div className="support-capture-gesture-orb">
              {captureGesture.mode === 'circle' ? <IconCircle /> : <IconMic />}
            </div>
          </div>
        </div>
      ) : null}
      {lightbox ? <MediaLightbox items={lightbox.items} index={lightbox.index} onClose={closeLightbox} /> : null}
      {menu ? (
        <MessageActionsMenu
          open
          x={menu.x}
          y={menu.y}
          canEdit={
            menu.message.author_role === 'user' &&
            Number(user?.telegram_user_id) === Number(menu.message.author_telegram_id) &&
            !isForwardedMessage(menu.message) &&
            (menu.message.message_type || 'text') === 'text' &&
            detail?.status !== 'closed'
          }
          canDelete={
            menu.message.author_role === 'user' &&
            Number(user?.telegram_user_id) === Number(menu.message.author_telegram_id) &&
            detail?.status !== 'closed'
          }
          canForward={detail?.status !== 'closed' && menu.message.id > 0}
          canSelect={detail?.status !== 'closed' && menu.message.id > 0}
          canCopy={Boolean((menu.message.body || '').trim())}
          canReact={detail?.status !== 'closed' && menu.message.id > 0}
          onClose={() => setMenu(null)}
          onReact={(emoji) => void toggleReaction(menu.message.id, emoji)}
          onAction={(action) => void runMessageAction(action, menu.message)}
        />
      ) : null}
    </div>
  );
}
